// Labour Payments — day-wise wage register (effective units × per-day amount)
// + multi-select payment requests flowing the labour_payment approval matrix.
// Rates default from the Labour Library and can be overridden per day until
// the row is locked by a request. Paid rows lock permanently.
import { useCallback, useEffect, useMemo, useState, Fragment } from "react";
import { usePager, Paginator } from "@/components/Paginator";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatINR } from "@/lib/api";
import { toast } from "sonner";
import { X, FileSpreadsheet, Pencil, Check, Send, BadgeCheck, ChevronDown, ChevronRight, Paperclip, Search, Eye } from "lucide-react";

const openSheetAttachment = async (projectId, sheetId) => {
  try {
    const r = await api.get(`/projects/${projectId}/labour-attendance/${sheetId}/attachment`, { responseType: "blob" });
    const url = URL.createObjectURL(r.data);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  } catch (e) { toast.error(e?.response?.data?.detail || "Failed to open attendance attachment"); }
};

const inputCls = "border border-slate-300 rounded px-2 py-1.5 text-xs w-full";
const today = () => new Date().toLocaleDateString("en-CA");
const monthStart = () => today().slice(0, 8) + "01";
const inr = (v) => formatINR(v ?? 0);
const fmtD = (d) => {
  if (!d) return "—";
  const [y, m, dd] = d.split("-");
  return `${dd}-${["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][+m] || m}-${y?.slice(2)}`;
};

const STATUS_CHIP = {
  "Pending Approval": "bg-amber-100 text-amber-800 border-amber-300",
  Approved: "bg-sky-100 text-sky-800 border-sky-300",
  Paid: "bg-emerald-100 text-emerald-800 border-emerald-300",
  Rejected: "bg-rose-100 text-rose-800 border-rose-300",
};

function StatusChip({ status }) {
  return (
    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold border whitespace-nowrap ${STATUS_CHIP[status] || "bg-slate-100 text-slate-600 border-slate-300"}`}>
      {status}
    </span>
  );
}

function RequestPaymentModal({ project, selected, onClose, onDone }) {
  const [remark, setRemark] = useState("");
  const [busy, setBusy] = useState(false);
  const total = selected.reduce((a, r) => a + (r.amount || 0), 0);
  const labourCount = new Set(selected.map((r) => r.labour_ref_id)).size;
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/projects/${project.id}/labour-payment-requests`, {
        remark: remark.trim(),
        rows: selected.map((x) => ({ attendance_date: x.attendance_date, labour_ref_id: x.labour_ref_id })),
      });
      toast.success(`Payment request ${r.data.request_number} raised (${r.data.status})`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Request failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="lp-request-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">Request Labour Payment</div>
          <button data-testid="lp-request-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div className="border border-slate-200 rounded max-h-52 overflow-y-auto">
            <table className="w-full text-[11px]">
              <thead className="bg-slate-50 text-[9px] uppercase text-slate-500 sticky top-0">
                <tr><th className="px-2 py-1 text-left">Date</th><th className="px-2 py-1 text-left">Labour</th>
                  <th className="px-2 py-1 text-left">Type</th>
                  <th className="px-2 py-1 text-right">Units</th><th className="px-2 py-1 text-right">Rate</th>
                  <th className="px-2 py-1 text-right">Amount</th></tr>
              </thead>
              <tbody>
                {selected.map((r) => (
                  <tr key={`${r.attendance_date}|${r.labour_ref_id}`} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono">{r.attendance_date}</td>
                    <td className="px-2 py-1">{r.labour_code} · {r.name}</td>
                    <td className="px-2 py-1" data-testid={`lp-request-type-${r.attendance_date}-${r.labour_code}`}>{r.labour_type || "—"}</td>
                    <td className="px-2 py-1 text-right">{r.units}</td>
                    <td className="px-2 py-1 text-right">{inr(r.per_day_amount)}</td>
                    <td className="px-2 py-1 text-right font-bold">{inr(r.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="text-right text-sm font-black text-slate-900" data-testid="lp-request-total">
            {labourCount} labour{labourCount > 1 ? "s" : ""} · {selected.length} day{selected.length > 1 ? "s" : ""} · Total {inr(total)}
          </div>
          <label className="block space-y-1"><span className="font-bold text-slate-600">Remark (optional)</span>
            <textarea data-testid="lp-request-remark" className={`${inputCls} h-16`} value={remark} onChange={(e) => setRemark(e.target.value)} /></label>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
            <button data-testid="lp-request-submit" disabled={busy} onClick={submit}
              className="px-4 py-1.5 bg-[#0B1F3A] text-white text-xs font-bold rounded disabled:opacity-50 inline-flex items-center gap-1.5">
              <Send size={12} /> {busy ? "Submitting…" : "Submit for Approval"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarkPaidModal({ project, req, onClose, onDone }) {
  const [f, setF] = useState({ payment_date: today(), payment_reference: "", remark: "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.payment_date) return toast.error("Payment date is mandatory");
    setBusy(true);
    try {
      await api.post(`/projects/${project.id}/labour-payment-requests/${req.id}/mark-paid`, f);
      toast.success(`${req.request_number} marked Paid`);
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mark paid failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-10" data-testid="lp-paid-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900">Mark Paid — {req.request_number}</div>
          <button data-testid="lp-paid-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 text-xs">
          <div className="text-slate-600">Total: <b className="text-slate-900">{inr(req.total_amount)}</b> · {req.row_count} day{req.row_count > 1 ? "s" : ""}</div>
          <label className="block space-y-1"><span className="font-bold text-slate-600">Payment Date *</span>
            <input data-testid="lp-paid-date" type="date" max={today()} className={inputCls} value={f.payment_date}
              onChange={(e) => setF({ ...f, payment_date: e.target.value })} /></label>
          <label className="block space-y-1"><span className="font-bold text-slate-600">Reference / UTR (optional)</span>
            <input data-testid="lp-paid-ref" className={inputCls} value={f.payment_reference}
              onChange={(e) => setF({ ...f, payment_reference: e.target.value })} /></label>
          <label className="block space-y-1"><span className="font-bold text-slate-600">Remark (optional)</span>
            <input data-testid="lp-paid-remark" className={inputCls} value={f.remark}
              onChange={(e) => setF({ ...f, remark: e.target.value })} /></label>
          <div className="text-[10px] text-slate-400">Operational record only — no accounting entry is posted. Paid days lock permanently.</div>
          <div className="flex justify-end gap-2">
            <button onClick={onClose} className="px-3 py-1.5 border rounded text-xs">Cancel</button>
            <button data-testid="lp-paid-submit" disabled={busy} onClick={submit}
              className="px-4 py-1.5 bg-emerald-700 text-white text-xs font-bold rounded disabled:opacity-50 inline-flex items-center gap-1.5">
              <BadgeCheck size={13} /> {busy ? "Saving…" : "Mark Paid"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function LabourRequestViewModal({ project, req, onClose }) {
  const period = req.date_from
    ? (req.date_from === req.date_to ? fmtD(req.date_from) : `${fmtD(req.date_from)} – ${fmtD(req.date_to)}`)
    : "—";
  const Info = ({ label, children, tid }) => (
    <div>
      <div className="text-[9px] uppercase font-bold text-slate-400">{label}</div>
      <div className="text-xs text-slate-800 font-semibold" data-testid={tid}>{children}</div>
    </div>
  );
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center overflow-y-auto py-8" data-testid="lp-view-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl mx-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="text-sm font-bold text-slate-900 inline-flex items-center gap-2">
            Labour Payment Request — <span className="font-mono">{req.request_number}</span> <StatusChip status={req.status} />
          </div>
          <button data-testid="lp-view-close" onClick={onClose} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
            <Info label="Total Amount" tid="lp-view-amount"><span className="text-sm font-black">{inr(req.total_amount)}</span></Info>
            <Info label="Labour Count" tid="lp-view-labours">{req.labour_count ?? "—"}</Info>
            <Info label="Labour Days" tid="lp-view-days">{req.row_count} day{req.row_count > 1 ? "s" : ""} · {req.total_units} unit{req.total_units === 1 ? "" : "s"}</Info>
            <Info label="Date Range" tid="lp-view-period">{period}</Info>
            <Info label="Tag(s)" tid="lp-view-tags">{(req.tags || []).join(", ") || "—"}</Info>
            <Info label="Requested By" tid="lp-view-requested-by">{req.requested_by}<div className="text-[9px] text-slate-400 font-normal">{(req.requested_at || "").slice(0, 10)}</div></Info>
            {req.status === "Pending Approval" && (
              <Info label="Pending With" tid="lp-view-pending-with"><span className="text-amber-800">{req.pending_with || "—"}</span></Info>
            )}
            {req.status === "Paid" && (
              <Info label="Payment" tid="lp-view-payment"><span className="text-emerald-800">{req.payment_date}{req.payment_reference ? ` · ${req.payment_reference}` : ""}</span></Info>
            )}
            {req.status === "Rejected" && (
              <Info label="Rejection Reason" tid="lp-view-rejection"><span className="text-rose-700">{req.rejection_reason || "—"}</span></Info>
            )}
          </div>
          <div className="border border-slate-200 rounded max-h-72 overflow-y-auto">
            <table className="w-full text-[11px]" data-testid="lp-view-rows-table">
              <thead className="bg-slate-50 text-[9px] uppercase text-slate-500 sticky top-0">
                <tr><th className="px-2 py-1.5 text-left">Date</th><th className="px-2 py-1.5 text-left">Labour</th>
                  <th className="px-2 py-1.5 text-left">Skill</th>
                  <th className="px-2 py-1.5 text-right">OT</th><th className="px-2 py-1.5 text-right">Units</th>
                  <th className="px-2 py-1.5 text-right">Per Day</th><th className="px-2 py-1.5 text-right">Amount</th>
                  <th className="px-2 py-1.5 text-right">Att.</th></tr>
              </thead>
              <tbody>
                {(req.rows || []).map((x) => (
                  <tr key={x.row_key} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono whitespace-nowrap">{x.attendance_date}</td>
                    <td className="px-2 py-1">{x.labour_code} · {x.labour_name}</td>
                    <td className="px-2 py-1">{x.skill_category || "—"}</td>
                    <td className="px-2 py-1 text-right">{x.overtime || 0}</td>
                    <td className="px-2 py-1 text-right">{x.units}</td>
                    <td className="px-2 py-1 text-right">{inr(x.per_day_amount)}</td>
                    <td className="px-2 py-1 text-right font-bold">{inr(x.amount)}</td>
                    <td className="px-2 py-1 text-right">
                      {x.sheet_id && x.attachment_filename ? (
                        <button data-testid={`lp-view-att-${x.row_key}`} title={`View attendance attachment (${x.attachment_filename})`}
                          onClick={() => openSheetAttachment(project.id, x.sheet_id)} className="text-sky-700 hover:text-sky-900"><Paperclip size={12} /></button>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {req.remark && <div className="text-[10px] text-slate-500">Remark: <i>{req.remark}</i></div>}
          <div className="flex justify-end">
            <button data-testid="lp-view-close-btn" onClick={onClose} className="px-4 py-1.5 border rounded text-xs font-bold">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function RequestsPanel({ project, refreshKey, onChanged }) {
  const { hasPerm } = useAuth();
  const canPay = hasPerm("labour_payment", "post_payment");
  const [data, setData] = useState({ rows: [] });
  const [open, setOpen] = useState({});
  const [paying, setPaying] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("");
  const load = useCallback(() => {
    api.get(`/projects/${project.id}/labour-payment-requests`, { params: { limit: 100 } })
      .then((r) => setData(r.data || { rows: [] }))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load requests"));
  }, [project.id]);
  useEffect(load, [load, refreshKey]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data.rows || []).filter((r) => {
      if (statusF && r.status !== statusF) return false;
      if (!s) return true;
      return [r.request_number, r.requested_by, r.remark, r.payment_reference,
        r.pending_with, ...(r.tags || []),
        ...(r.rows || []).flatMap((x) => [x.labour_code, x.labour_name])]
        .join(" ").toLowerCase().includes(s);
    });
  }, [data, q, statusF]);
  const pager = usePager(filtered, 10);
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <input data-testid="lp-req-search" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Search request #, labour, requested by…"
          className="border border-slate-300 rounded px-2 py-1.5 w-64" />
        <select data-testid="lp-req-status-filter" value={statusF} onChange={(e) => setStatusF(e.target.value)}
          className="border border-slate-300 rounded px-2 py-1.5 bg-white">
          <option value="">All Statuses</option>
          <option>Pending Approval</option>
          <option>Approved</option>
          <option>Paid</option>
          <option>Rejected</option>
        </select>
        {(q || statusF) && (
          <span className="text-[11px] text-slate-500" data-testid="lp-req-filter-count">
            {filtered.length} of {(data.rows || []).length} request{(data.rows || []).length === 1 ? "" : "s"}
          </span>
        )}
      </div>
      <div className="border border-slate-200 rounded overflow-x-auto">
        <table className="w-full min-w-[1180px] text-xs" data-testid="lp-requests-table">
          <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
            <tr><th className="px-3 py-2 text-left">Request #</th><th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Pending With</th>
              <th className="px-3 py-2 text-right">Labours</th>
              <th className="px-3 py-2 text-right">Days</th><th className="px-3 py-2 text-right">Units</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Tag(s)</th>
              <th className="px-3 py-2 text-left">Period</th>
              <th className="px-3 py-2 text-left">Requested By</th>
              <th className="px-3 py-2 text-left">Payment</th><th className="px-3 py-2 text-right"></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={12} className="px-3 py-8 text-center text-slate-400" data-testid="lp-requests-empty">
                {(data.rows || []).length === 0 ? "No payment requests yet." : "No requests match the search / status filter."}</td></tr>
            )}
            {pager.slice.map((r) => (
              <Fragment key={r.id}>
                <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`lp-req-row-${r.request_number?.replace(/\//g, "-")}`}>
                  <td className="px-3 py-2 font-mono font-bold whitespace-nowrap">
                    <button onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))} className="inline-flex items-center gap-1 hover:underline" data-testid={`lp-req-expand-${r.request_number?.replace(/\//g, "-")}`}>
                      {open[r.id] ? <ChevronDown size={12} /> : <ChevronRight size={12} />} {r.request_number}
                    </button>
                  </td>
                  <td className="px-3 py-2"><StatusChip status={r.status} /></td>
                  <td className="px-3 py-2 max-w-[140px]">
                    {r.status === "Pending Approval"
                      ? <span className="text-amber-800 font-bold truncate block" title={r.pending_with} data-testid={`lp-req-pending-with-${r.request_number?.replace(/\//g, "-")}`}>{r.pending_with || "—"}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right" data-testid={`lp-req-labours-${r.request_number?.replace(/\//g, "-")}`}>{r.labour_count ?? "—"}</td>
                  <td className="px-3 py-2 text-right">{r.row_count}</td>
                  <td className="px-3 py-2 text-right">{r.total_units}</td>
                  <td className="px-3 py-2 text-right font-bold tabular-nums">{inr(r.total_amount)}</td>
                  <td className="px-3 py-2 max-w-[130px]">
                    <span className="truncate block" title={(r.tags || []).join(", ")} data-testid={`lp-req-tags-${r.request_number?.replace(/\//g, "-")}`}>{(r.tags || []).join(", ") || "—"}</span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-[10px]" data-testid={`lp-req-period-${r.request_number?.replace(/\//g, "-")}`}>
                    {r.date_from ? (r.date_from === r.date_to ? fmtD(r.date_from) : `${fmtD(r.date_from)} – ${fmtD(r.date_to)}`) : "—"}
                  </td>
                  <td className="px-3 py-2">{r.requested_by}<div className="text-[9px] text-slate-400">{(r.requested_at || "").slice(0, 10)}</div></td>
                  <td className="px-3 py-2 text-[10px]">
                    {r.status === "Paid"
                      ? <span className="text-emerald-800">{r.payment_date}{r.payment_reference ? ` · ${r.payment_reference}` : ""}</span>
                      : r.status === "Rejected"
                        ? <span className="text-rose-700" title={r.rejection_reason}>{(r.rejection_reason || "").slice(0, 40)}</span>
                        : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <button data-testid={`lp-req-view-${r.request_number?.replace(/\//g, "-")}`} onClick={() => setViewing(r)} title="View payment request"
                      className="text-slate-500 hover:text-slate-900 mr-2 align-middle"><Eye size={13} /></button>
                    {r.status === "Approved" && canPay && (
                      <button data-testid={`lp-mark-paid-${r.request_number?.replace(/\//g, "-")}`} onClick={() => setPaying(r)}
                        className="text-emerald-800 font-bold hover:underline inline-flex items-center gap-1"><BadgeCheck size={12} /> Mark Paid</button>
                    )}
                  </td>
                </tr>
                {open[r.id] && (
                  <tr className="bg-slate-50/60">
                    <td colSpan={12} className="px-6 py-2">
                      <table className="w-full text-[11px]" data-testid={`lp-req-detail-${r.request_number?.replace(/\//g, "-")}`}>
                        <thead className="text-[9px] uppercase text-slate-400">
                          <tr><th className="py-1 text-left">Date</th><th className="py-1 text-left">Labour</th>
                            <th className="py-1 text-left">Skill</th>
                            <th className="py-1 text-left">Cat 1</th><th className="py-1 text-left">Cat 2</th>
                            <th className="py-1 text-right">OT</th>
                            <th className="py-1 text-right">Units</th><th className="py-1 text-right">Per Day</th>
                            <th className="py-1 text-right">Amount</th><th className="py-1 text-right">Att.</th></tr>
                        </thead>
                        <tbody>
                          {(r.rows || []).map((x) => (
                            <tr key={x.row_key} className="border-t border-slate-100">
                              <td className="py-1 font-mono">{x.attendance_date}</td>
                              <td className="py-1">{x.labour_code} · {x.labour_name}</td>
                              <td className="py-1">{x.skill_category || "—"}</td>
                              <td className="py-1">{x.category1_name || "—"}</td>
                              <td className="py-1">{x.category2_name || "—"}</td>
                              <td className="py-1 text-right">{x.overtime || 0}</td>
                              <td className="py-1 text-right">{x.units}</td>
                              <td className="py-1 text-right">{inr(x.per_day_amount)}</td>
                              <td className="py-1 text-right font-bold">{inr(x.amount)}</td>
                              <td className="py-1 text-right">
                                {x.sheet_id && x.attachment_filename ? (
                                  <button data-testid={`lp-req-att-${x.row_key}`} title={`View attendance attachment (${x.attachment_filename})`}
                                    onClick={() => openSheetAttachment(project.id, x.sheet_id)} className="text-sky-700 hover:text-sky-900"><Paperclip size={12} /></button>
                                ) : <span className="text-slate-300">—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {r.remark && <div className="text-[10px] text-slate-500 mt-1">Remark: <i>{r.remark}</i></div>}
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        <Paginator pager={pager} testid="lp-requests-pager" />
      </div>
      {paying && <MarkPaidModal project={project} req={paying} onClose={() => setPaying(null)}
        onDone={() => { setPaying(null); load(); onChanged?.(); }} />}
      {viewing && <LabourRequestViewModal project={project} req={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

export default function LabourPaymentsTab({ project }) {
  const { hasPerm } = useAuth();
  const canEdit = hasPerm("labour_payment", "edit");
  const canCreate = hasPerm("labour_payment", "create");
  const [view, setView] = useState("wages");
  const [range, setRange] = useState({ from: monthStart(), to: today() });
  const [data, setData] = useState(null);
  const [sel, setSel] = useState({});
  const [editKey, setEditKey] = useState(null);
  const [editVal, setEditVal] = useState("");
  const [requesting, setRequesting] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [err, setErr] = useState("");
  const [cats, setCats] = useState({ cat1: [], cat2: [] });
  // Aug-2026 (user request) — search filter (name / tag / ID); ticking then
  // acts only on what the user selected, filter or not.
  const [q, setQ] = useState("");

  useEffect(() => {
    api.get("/grn-categories").then((r) => {
      const list = Array.isArray(r.data) ? r.data : [];
      setCats({
        cat1: list.filter((x) => x.kind === "cat1" && x.active !== false),
        cat2: list.filter((x) => x.kind === "cat2" && x.active !== false),
      });
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    api.get(`/projects/${project.id}/labour-wages`, {
      params: { from_date: range.from || undefined, to_date: range.to || undefined } })
      .then((r) => { setData(r.data); setErr(""); setSel({}); })
      .catch((e) => { setErr(e?.response?.data?.detail || "Failed to load labour wages"); setData(null); });
  }, [project.id, range]);
  useEffect(() => { if (view === "wages") load(); }, [view, load]);

  const rows = useMemo(() => data?.rows || [], [data]);
  const keyOf = (r) => `${r.attendance_date}|${r.labour_ref_id}`;
  const qt = q.trim().toLowerCase();
  const visible = useMemo(() => !qt ? rows : rows.filter((r) =>
    [r.name, r.tag, r.labour_code].some((v) => String(v || "").toLowerCase().includes(qt))), [rows, qt]);
  const selectable = visible.filter((r) => !r.locked && r.per_day_amount > 0);
  const selectedRows = useMemo(() => rows.filter((r) => sel[keyOf(r)]), [rows, sel]);
  const selectedTotal = selectedRows.reduce((a, r) => a + (r.amount || 0), 0);
  // Aug-2026 (user request) — selection summary also shows total labour DAYS
  // (effective units incl. OT) and distinct LABOUR count, not just amount.
  const selectedDays = selectedRows.reduce((a, r) => a + (Number(r.units) || 0), 0);
  const selectedLabourCount = new Set(selectedRows.map((r) => r.labour_ref_id)).size;
  const pager = usePager(visible, 25);

  const toggleAll = () => {
    const allSel = selectable.length > 0 && selectable.every((r) => sel[keyOf(r)]);
    setSel((m) => {
      const n = { ...m };
      selectable.forEach((r) => { if (allSel) delete n[keyOf(r)]; else n[keyOf(r)] = true; });
      return n;
    });
  };

  const saveRate = async (r) => {
    const v = parseFloat(editVal);
    if (isNaN(v) || v < 0) return toast.error("Enter a valid per-day amount");
    try {
      await api.put(`/projects/${project.id}/labour-wages/rate`, {
        attendance_date: r.attendance_date, labour_ref_id: r.labour_ref_id, per_day_amount: v });
      toast.success(`Per-day amount updated for ${r.labour_code} · ${r.attendance_date}`);
      setEditKey(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Rate update failed"); }
  };

  const saveCategory = async (r, field, value) => {
    try {
      await api.put(`/projects/${project.id}/labour-wages/rate`, {
        attendance_date: r.attendance_date, labour_ref_id: r.labour_ref_id, [field]: value });
      toast.success(`${field === "category1_id" ? "Category 1" : "Category 2"} ${value ? "overridden" : "reset to attendance value"} for ${r.labour_code} · ${r.attendance_date}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Category update failed"); }
  };

  const doExport = async () => {
    try {
      const r = await api.get(`/projects/${project.id}/labour-wages-export`, {
        responseType: "blob",
        params: { from_date: range.from || undefined, to_date: range.to || undefined } });
      const url = URL.createObjectURL(r.data);
      const link = document.createElement("a");
      link.href = url; link.download = `labour_wages_${project.project_code}.xlsx`; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { toast.error(e?.response?.data?.detail || "Export failed (export permission required)"); }
  };

  const openAttachment = (sheetId) => openSheetAttachment(project.id, sheetId);

  return (
    <div className="space-y-3" data-testid="lp-tab">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 border-b border-slate-200 text-xs">
          {[["wages", "Day-wise Wages"], ["requests", "Payment Requests"]].map(([k, label]) => (
            <button key={k} data-testid={`lp-view-${k}`} onClick={() => setView(k)}
              className={`px-3 py-1.5 font-bold border-b-2 -mb-px ${view === k ? "border-[#F97316] text-[#0B1F3A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
              {label}
            </button>
          ))}
        </div>
        <button data-testid="lp-export-btn" onClick={doExport}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-emerald-800 border border-emerald-300 rounded hover:bg-emerald-50">
          <FileSpreadsheet size={13} /> Export Excel
        </button>
      </div>

      {view === "wages" && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 flex-wrap text-xs">
            <input data-testid="lp-from" type="date" className="border border-slate-300 rounded px-2 py-1.5"
              value={range.from} onChange={(e) => setRange({ ...range, from: e.target.value })} />
            <span className="text-slate-400">to</span>
            <input data-testid="lp-to" type="date" className="border border-slate-300 rounded px-2 py-1.5"
              value={range.to} onChange={(e) => setRange({ ...range, to: e.target.value })} />
            <div className="relative">
              <Search size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input data-testid="lp-search" className="border border-slate-300 rounded pl-6 pr-2 py-1.5 text-xs w-56"
                placeholder="Search labour name / tag / ID…"
                value={q} onChange={(e) => setQ(e.target.value)} />
            </div>
            {qt && <span className="text-[11px] text-slate-500" data-testid="lp-search-count">{visible.length} of {rows.length} shown</span>}
            {data && (
              <span className="text-[11px] text-slate-500 ml-2" data-testid="lp-totals">
                {data.totals.row_count} rows · payable <b className="text-slate-800">{inr(data.totals.payable_amount)}</b>
              </span>
            )}
          </div>

          {err && <div className="border border-amber-300 bg-amber-50 rounded p-3 text-xs text-amber-800" data-testid="lp-error">{err}</div>}
          {data && rows.length === 0 && (
            <div className="border border-slate-200 bg-slate-50/60 rounded py-10 text-center text-xs text-slate-400" data-testid="lp-empty">
              No payable attendance (effective count &gt; 0) in this range.
            </div>
          )}

          {rows.length > 0 && (
            <>
              <div className="border border-slate-200 rounded overflow-x-auto">
                <table className="w-full min-w-[900px] text-xs" data-testid="lp-wages-table">
                  <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                    <tr>
                      <th className="px-2 py-2">
                        <input type="checkbox" data-testid="lp-select-all"
                          checked={selectable.length > 0 && selectable.every((r) => sel[keyOf(r)])}
                          disabled={!canCreate || selectable.length === 0} onChange={toggleAll} />
                      </th>
                      <th className="px-3 py-2 text-left">Date</th><th className="px-3 py-2 text-left">Labour</th>
                      <th className="px-3 py-2 text-left">Type</th>
                      <th className="px-3 py-2 text-left">Tag</th>
                      <th className="px-3 py-2 text-left">Skill</th>
                      <th className="px-3 py-2 text-left">Cat 1</th><th className="px-3 py-2 text-left">Cat 2</th>
                      <th className="px-3 py-2 text-left">Status</th>
                      <th className="px-3 py-2 text-right">OT</th><th className="px-3 py-2 text-right">Effective</th>
                      <th className="px-3 py-2 text-right">Per Day ₹</th><th className="px-3 py-2 text-right">Amount</th>
                      <th className="px-3 py-2 text-left">Payment</th>
                      <th className="px-3 py-2 text-right">Att.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pager.slice.map((r) => {
                      const k = keyOf(r);
                      return (
                        <tr key={k} className={`border-t border-slate-100 ${r.locked ? "bg-slate-50/70" : "hover:bg-slate-50"}`} data-testid={`lp-row-${r.attendance_date}-${r.labour_code}`}>
                          <td className="px-2 py-2">
                            <input type="checkbox" data-testid={`lp-check-${r.attendance_date}-${r.labour_code}`}
                              checked={!!sel[k]} disabled={!canCreate || r.locked || r.per_day_amount <= 0}
                              onChange={(e) => setSel((s) => ({ ...s, [k]: e.target.checked }))} />
                          </td>
                          <td className="px-3 py-2 font-mono font-bold whitespace-nowrap">{r.attendance_date}</td>
                          <td className="px-3 py-2"><span className="font-mono font-bold">{r.labour_code}</span> · {r.name}</td>
                          <td className="px-3 py-2 whitespace-nowrap" data-testid={`lp-type-${r.attendance_date}-${r.labour_code}`}>{r.labour_type || "—"}</td>
                          <td className="px-3 py-2" data-testid={`lp-tag-${r.attendance_date}-${r.labour_code}`}>
                            {r.tag ? <span className="inline-block bg-indigo-50 border border-indigo-200 text-indigo-800 rounded px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap">{r.tag}</span> : "—"}
                          </td>
                          <td className="px-3 py-2">{r.skill_category || "—"}</td>
                          <td className="px-3 py-2" data-testid={`lp-cat1-${r.attendance_date}-${r.labour_code}`}>
                            {canEdit && !r.locked ? (
                              <select data-testid={`lp-cat1-sel-${r.attendance_date}-${r.labour_code}`}
                                className={`border rounded px-1 py-0.5 text-[11px] bg-white max-w-[120px] ${r.category_overridden ? "border-violet-300" : "border-slate-200"}`}
                                value={r.category1_id || ""} title="Override Category 1 for this day"
                                onChange={(e) => saveCategory(r, "category1_id", e.target.value)}>
                                <option value="">—</option>
                                {cats.cat1.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                            ) : (r.category1_name || "—")}
                          </td>
                          <td className="px-3 py-2" data-testid={`lp-cat2-${r.attendance_date}-${r.labour_code}`}>
                            {canEdit && !r.locked ? (
                              <select data-testid={`lp-cat2-sel-${r.attendance_date}-${r.labour_code}`}
                                className={`border rounded px-1 py-0.5 text-[11px] bg-white max-w-[120px] ${r.category_overridden ? "border-violet-300" : "border-slate-200"}`}
                                value={r.category2_id || ""} title="Override Category 2 for this day"
                                onChange={(e) => saveCategory(r, "category2_id", e.target.value)}>
                                <option value="">—</option>
                                {cats.cat2.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                              </select>
                            ) : (r.category2_name || "—")}
                          </td>
                          <td className="px-3 py-2">{r.status}</td>
                          <td className="px-3 py-2 text-right">{r.overtime > 0 ? <span className="text-orange-700 font-bold">+{r.overtime}</span> : "—"}</td>
                          <td className="px-3 py-2 text-right font-bold">{r.units}</td>
                          <td className="px-3 py-2 text-right whitespace-nowrap">
                            {editKey === k ? (
                              <span className="inline-flex items-center gap-1">
                                <input data-testid={`lp-rate-input-${r.attendance_date}-${r.labour_code}`} type="number" min="0" step="any" autoFocus
                                  className="border border-slate-300 rounded px-1 py-0.5 text-xs w-20 text-right"
                                  value={editVal} onChange={(e) => setEditVal(e.target.value)}
                                  onKeyDown={(e) => { if (e.key === "Enter") saveRate(r); if (e.key === "Escape") setEditKey(null); }} />
                                <button data-testid={`lp-rate-save-${r.attendance_date}-${r.labour_code}`} onClick={() => saveRate(r)} className="text-emerald-700"><Check size={13} /></button>
                                <button onClick={() => setEditKey(null)} className="text-slate-400"><X size={12} /></button>
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 tabular-nums">
                                {r.per_day_amount > 0 ? inr(r.per_day_amount) : <span className="text-rose-600 font-bold" title="Set the per-day amount in the Labour Library or override it here">not set</span>}
                                {r.rate_source === "override" && <span className="text-[8px] uppercase font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1" title="Day-wise override">ovr</span>}
                                {canEdit && !r.locked && (
                                  <button data-testid={`lp-rate-edit-${r.attendance_date}-${r.labour_code}`} title="Override per-day amount for this day"
                                    onClick={() => { setEditKey(k); setEditVal(r.per_day_amount || ""); }} className="text-slate-400 hover:text-slate-800"><Pencil size={11} /></button>
                                )}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-right font-bold tabular-nums">{r.amount > 0 ? inr(r.amount) : "—"}</td>
                          <td className="px-3 py-2">
                            {r.locked ? (
                              <span className="inline-flex items-center gap-1 whitespace-nowrap" data-testid={`lp-lock-${r.attendance_date}-${r.labour_code}`}>
                                <span className="font-mono text-[10px]">{r.lock?.request_number}</span>
                                <StatusChip status={r.lock?.request_status || "Locked"} />
                              </span>
                            ) : <span className="text-slate-400 text-[10px]">available</span>}
                          </td>
                          <td className="px-3 py-2 text-right">
                            {r.attachment && r.sheet_id ? (
                              <button data-testid={`lp-att-${r.attendance_date}-${r.labour_code}`} title={`View attendance attachment (${r.attachment.filename})`}
                                onClick={() => openAttachment(r.sheet_id)} className="text-sky-700 hover:text-sky-900"><Paperclip size={13} /></button>
                            ) : <span className="text-slate-300" title="No attachment on this attendance sheet">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <Paginator pager={pager} testid="lp-wages-pager" />
              </div>
              {canCreate && (
                <div className="flex items-center justify-between gap-2 flex-wrap sticky bottom-0 bg-white border border-slate-200 rounded px-3 py-2" data-testid="lp-selection-bar">
                  <div className="text-xs text-slate-600">
                    Selected: <b data-testid="lp-selected-labour-count">{selectedLabourCount}</b> labour{selectedLabourCount === 1 ? "" : "s"} · <b data-testid="lp-selected-days">{Math.round(selectedDays * 100) / 100}</b> labour day{selectedDays === 1 ? "" : "s"} · <b className="text-slate-900" data-testid="lp-selected-amount">{inr(selectedTotal)}</b>
                  </div>
                  <button data-testid="lp-request-btn" disabled={selectedRows.length === 0} onClick={() => setRequesting(true)}
                    className="px-4 py-1.5 bg-[#0B1F3A] text-white text-xs font-bold rounded disabled:opacity-40 inline-flex items-center gap-1.5">
                    <Send size={12} /> Request Payment
                  </button>
                </div>
              )}
              <div className="text-[9px] text-slate-400">
                Amount = effective attendance (incl. overtime) × per-day amount · requests flow the Labour Payment approval matrix · Paid days lock permanently — no accounting entry is posted.
              </div>
            </>
          )}
        </div>
      )}

      {view === "requests" && <RequestsPanel project={project} refreshKey={refreshKey} onChanged={() => setRefreshKey((k) => k + 1)} />}

      {requesting && (
        <RequestPaymentModal project={project} selected={selectedRows} onClose={() => setRequesting(false)}
          onDone={() => { setRequesting(false); setSel({}); load(); setRefreshKey((k) => k + 1); }} />
      )}
    </div>
  );
}
